import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import MenuHeader from './MenuHeader';
import Nav from './Nav';
import croix from "../assets/images/close-black.png";
import btnInfo from "../assets/images/icon-info.svg";
import Modal from "react-modal";
import goback from '../assets/images/goback.svg';
import useApi from '../hooks/useApi';
import DomainService from '../services/domain.service';
import CategoryService from '../services/category.service';
import ProductService from '../services/product.service';
import AuthService from '../services/auth.service';

import { useNotifications } from '../context/NotificationProvider';
import useAuth from '../hooks/useAuth';

import remove from '../assets/images/remove.png';
import people from '../assets/images/people.png';
import { Button, Container, createTheme, IconButton, ThemeProvider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { usePrompt } from '../hooks/usePrompt';
import { useTranslatableData } from "../hooks/useTranslatable";
import { createEventDedupeKey, pushDataLayerEvent } from "../utils/gtmDataLayer";
import {
  buildEcommerceObject,
  buildItemsFromSelectedServices,
  buildUserDataObject,
  calculateValueFromItems,
} from "../utils/gtmEcommerce";
import { buildGtmEventFields } from "../utils/gtmEventFields";

const EDIT_STEP_PATHS = [
  '/choose-location',
  '/choose-service',
  '/choose-therapist',
  '/payment'
];


const customStyles = {
  content: {
    width: "33%",
    top: "50%",
    padding: "40px",
    left: "50%",
    right: "auto",
    bottom: "auto",
    borderRadius: "10px",
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
    maxWidth: "450px",
    margin: "auto",
    textAlign: "center",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
  },
};

const theme = createTheme({
  palette: {
    white: {
      "main": "#FFFFFF",
      "light": "#FFFFFF",
      "dark": "#FFFFFF",
      "contrastText": "#FFFFFF"
    },
    dark_yellow: {
      "main": "#393D44",
      "light": "#393D44",
      "dark": "#393D44",
      "contrastText": "#FAEF9B",
    },
    dark_white: {
      "main": "#393D44",
      "light": "#393D44",
      "dark": "#393D44",
      "contrastText": "#FFFFFF",
    },
    dark: {
      "main": "#393D44",
      "light": "#393D44",
      "dark": "#393D44",
      "contrastText": "#393D44"
    }
  },
});

function ChooseService() {
  const { t, i18n } = useTranslation('service');
  const [domains, setDomains] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [modalIsOpenService, setIsOpenService] = useState(false);
  const closeModalservice = () => setIsOpenService(false);
  const openModalservice = () => setIsOpenService(true);
  const [hasModalShown, setHasModalShown] = useState(false);

  const api = useApi();
  const navigate = useNavigate();
  const notify = useNotifications();
  const { auth } = useAuth();
  const [trackingUser, setTrackingUser] = useState(auth?.user || null);
  const [showDescriptionFor, setShowDescriptionFor] = useState({});
  const [showProductList, setShowProductList] = useState({});
  const location = useLocation();
  const { selectedServices: prevSelectedServices, duo: prevDuo, address: prevAddress, phone: prevPhone } = location.state || {};
  const [duoModalOpenLocal, setDuoModalOpenLocal] = useState(false);
  const isEditMode = sessionStorage.getItem("editBookingMode") === "true" || (location?.state?.fromEditBooking);
  const savedState = JSON.parse(sessionStorage.getItem('state') || '{}');
  const savedOldBooking = JSON.parse(sessionStorage.getItem('oldBooking') || '{}');
  const phone = prevPhone || savedState?.phone || "";
  const locationState = location.state || {};
  const originallyDuo = savedOldBooking?.isDuo || prevDuo || savedState?.isDuo || false;
  const [isActive, setIsActive] = useState(originallyDuo || prevDuo || false);
  const updatedState = { ...savedState, ...locationState };
  const address = updatedState?.address || savedState?.address;
  const [selectedServices, setSelectedServices] = useState(() => {
    // Check if we have selectedServices in sessionStorage (from edit mode)
    const sessionState = JSON.parse(sessionStorage.getItem('state') || '{}');
    const existingServices = sessionState.selectedServices;
    if (existingServices && existingServices.length > 0 && sessionStorage.getItem("editBookingMode") === "true") {
      return existingServices.map((service) => ({
        domain: null,
        category: null,
        subCategory: null,
        product: service.product || service.service, // Pre-select the product
        cat_has_products: false,
        categories: [],
        sub_categories: [],
        products: [],
        sub_cat_has_products: false,
        cat_has_subcategories: false,
        showDescriptionForId: null,
        showDescriptionForType: null,
        showProductsForId: null,
        showProductsForType: null
      }));
    }
    // Otherwise use location state or default single service
    return prevSelectedServices || [
      { domain: null, category: null, subCategory: null, product: null, cat_has_products: false, categories: [], sub_categories: [], products: [], sub_cat_has_products: false, cat_has_subcategories: false, showDescriptionForId: null, showDescriptionForType: null, showProductsForId: null, showProductsForType: null }
    ];
  });

  const isEditBookingMode = sessionStorage.getItem("editBookingMode") === "true" || (location?.state?.fromEditBooking);

  // Precompute translated domains at the top level
  const translatedDomains = useTranslatableData(domains);

  // Precompute translated data for each person's service selection
  const translatedSelectedServices = useTranslatableData(selectedServices);

  const showDuoToggle = isEditMode 
    ? (originallyDuo || (savedOldBooking?.nbpeople >= 2))
    : (selectedServices.length === 2 || selectedServices.length === 4);

  usePrompt(
    isEditBookingMode,
    "Êtes-vous sûr de vouloir quitter la modification ? Vos changements ne seront pas enregistrés.",
    next => EDIT_STEP_PATHS.some(p => next.pathname.startsWith(p))
  );

  useEffect(() => {
    setTrackingUser(auth?.user || null);
  }, [auth?.user]);

  const hasPrimaryAddress = (user) =>
    Boolean(
      user?.primary_address?.id
      || user?.primary_address?.street
      || user?.primary_address?.zip_code
      || user?.primaryAddress?.id
      || user?.primaryAddress?.street
      || user?.primaryAddress?.zip_code
    );

  const resolveTrackingUser = async () => {
    if (!auth?.user?.id) {
      return trackingUser || auth?.user || null;
    }

    const currentUser = trackingUser || auth?.user;
    if (hasPrimaryAddress(currentUser)) {
      return currentUser;
    }

    try {
      const response = await AuthService.me(
        { id: auth.user.id, with: "primary_address" },
        api
      );
      const nextUser = response?.user || currentUser;
      setTrackingUser(nextUser || null);
      return nextUser || currentUser || null;
    } catch (_) {
      return currentUser || null;
    }
  };


  const fetchAllDomains = async () => {
    try {
      const response = await DomainService.index(
        { with: 'media,zones', sort: 'ordering' },
        api,
        !auth?.accessToken
      );

      const userZip = parseInt(address?.zip_code, 10);

      const filteredDomains = response.filter(domain =>
        domain.is_active && (
          domain.hotel_id == null &&
          domain.zones?.some(({ zip_code }) =>
            zip_code?.split(',').some(range => {
              const [min, max] = range.trim().split('-').map(Number);
              return max ? (userZip >= min && userZip <= max) : userZip === min;
            })
          )
        )
      );

      console.log({ filteredDomains });
      setDomains(filteredDomains);
    } catch (error) {
      console.error("Error fetching domains:", error);
    }
  };

  const fetchCategories = async (domainId) => {
    try {
      const response = await CategoryService.index(
        { is_hidden: false, domain_id: domainId, parent_id: null, is_active: 1, with: 'media,subcategories' },
        api,
        auth?.accessToken ? false : true
      );

      // subcategories with`is_hidden=false`
      const filteredCategories = response.map(category => ({
        ...category,
        sub_categories: category.subcategories
          ? category.subcategories.filter(sub => !sub.is_hidden)
          : []
      }));

      setCategories(filteredCategories);
      return filteredCategories;
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
    return [];
  };

  const fetchProducts = async (catId) => {
    try {
      const response = await ProductService.index(
        { is_hidden: false, category_id: catId, with: 'media', is_active: 1 },
        api,
        auth?.accessToken ? false : true
      );
      setProducts(response);
      return response;
    } catch (error) {
      console.error("Error fetching products:", error);
    }
    return [];
  };

  const fetchSubCategories = async (categoryId) => {
    try {
      const response = await CategoryService.index(
        { is_hidden: false, parent_id: categoryId, is_active: 1, with: 'media' },
        api,
        auth?.accessToken ? false : true
      );
      return response;
    } catch (error) {
      console.error("Error fetching subcategories:", error);
    }
    return [];
  };

  useEffect(() => {
    // const savedState = JSON.parse(sessionStorage.getItem('chooseServiceState') ?? '{}');
    // if (savedState.selectedServices) {
    //   setSelectedServices(savedState.selectedServices);
    // }
    // if (savedState.isActive !== undefined) {
    //   setIsActive(savedState.isActive);
    // }
    fetchAllDomains();
  }, []);

  // Auto-select hierarchy for edit mode
  useEffect(() => {
    const autoSelectHierarchy = async () => {
      if (!isEditBookingMode || !domains.length || selectedServices.every(s => s.domain)) return;

      const updatedServices = [...selectedServices];
      let hasChanges = false;

      for (let i = 0; i < selectedServices.length; i++) {
        const service = selectedServices[i];

        // Skip if already has domain or no product
        if (service.domain || !service.product) continue;

        const product = service.product;
        const domainId = product.domain_id;
        const productCategory = product.category;

        // Find the domain
        const domain = domains.find(d => d.id === domainId);
        if (!domain) continue;

        // Set domain and fetch categories
        updatedServices[i].domain = domain;
        const categories = await fetchCategories(domainId);
        updatedServices[i].categories = categories;

        // Check if the product's category is a top-level category or subcategory
        if (productCategory.parent_id === null) {
          // It's a top-level category
          const category = categories.find(c => c.id === productCategory.id);
          if (category) {
            updatedServices[i].category = category;

            // Fetch products and subcategories for this category
            const [products, subcats] = await Promise.all([
              fetchProducts(productCategory.id),
              fetchSubCategories(productCategory.id)
            ]);

            updatedServices[i].products = products;
            updatedServices[i].sub_categories = subcats;
            updatedServices[i].cat_has_products = products.length > 0;
            updatedServices[i].cat_has_subcategories = subcats.length > 0;
            updatedServices[i].showProductsForType = "category";
            updatedServices[i].showProductsForId = productCategory.id;
            updatedServices[i].showProductsForIdParent = null;
          }
        } else {
          // It's a subcategory - find the parent category first
          const parentCategory = categories.find(c => c.id === productCategory.parent_id);
          if (parentCategory) {
            updatedServices[i].category = parentCategory;

            // Fetch products and subcategories for the parent category
            const [products, subcats] = await Promise.all([
              fetchProducts(parentCategory.id),
              fetchSubCategories(parentCategory.id)
            ]);

            updatedServices[i].products = products;
            updatedServices[i].sub_categories = subcats;
            updatedServices[i].cat_has_products = products.length > 0;
            updatedServices[i].cat_has_subcategories = subcats.length > 0;

            // Now find and select the subcategory
            const subcategory = subcats.find(sc => sc.id === productCategory.id);
            if (subcategory) {
              updatedServices[i].subCategory = subcategory;

              // Fetch products for the subcategory
              const [subProducts, subProductsCat] = await Promise.all([
                fetchProducts(productCategory.id),
                fetchProducts(parentCategory.id)
              ]);

              updatedServices[i].products = subProducts;
              updatedServices[i].productsCat = subProductsCat;
              updatedServices[i].sub_cat_has_products = subProducts.length > 0;
              updatedServices[i].showProductsForType = "subCategory";
              updatedServices[i].showProductsForId = productCategory.id;
              updatedServices[i].showProductsForIdParent = parentCategory.id;
            }
          }
        }

        hasChanges = true;
      }

      if (hasChanges) {
        setSelectedServices(updatedServices);
      }
    };

    autoSelectHierarchy();
  }, [domains, isEditBookingMode]);

  const toggleSwitch = () => {
    if (isEditMode && !isActive && !originallyDuo) {
      try { sessionStorage.setItem('pendingDuoToggle', JSON.stringify({ requested: true })); } catch (err) {}
      setDuoModalOpenLocal(true);
      return;
    }
    setIsActive(!isActive);
  };


  // When select a domain
  const handleDomainSelect = async (personIndex, domainObj) => {
    const updated = [...selectedServices];
    console.log("CC dom", updated)
    console.log("cc domain persoin", personIndex)
    console.log({ domainObj })

    setShowDescriptionFor({ type: null, id: null });

    const categories = await fetchCategories(domainObj.id);
    console.log({ categories });

    updated[personIndex] = {
      domain: domainObj,
      category: null,
      subCategory: null,
      product: null,
      categories: categories,
      sub_categories: [],
      products: [],
      cat_has_products: false,
      sub_cat_has_products: false,
      cat_has_subcategories: false,
      showDescriptionForId: null,
      showDescriptionForType: null,
      showProductsForId: null,
      showProductsForIdParent: null,
      showProductsForType: null
    };

    setSelectedServices(updated);
    setSubCategories([]);
    setProducts([]);
  };

  const handleCategorySelect = async (personIndex, categoryObj) => {
    const updated = [...selectedServices];
    const currentlySelected = updated[personIndex].category?.id === categoryObj.id;
    console.log({ currentlySelected })
    if (currentlySelected) {
      updated[personIndex].category = null;
      updated[personIndex].subCategory = null;
      updated[personIndex].cat_has_products = false;
      updated[personIndex].sub_categories = [];
      updated[personIndex].sub_cat_has_products = false;
      updated[personIndex].showDescriptionForId = null;
      updated[personIndex].showDescriptionForType = null;
      updated[personIndex].showProductsForType = null;
      updated[personIndex].showProductsForId = null;
      updated[personIndex].products = [];
      updated[personIndex].product = null;
      updated[personIndex].cat_has_subcategories = false;

      setSelectedServices(updated);
      return;
    }
    updated[personIndex].category = categoryObj;
    updated[personIndex].subCategory = null;
    updated[personIndex].product = null;
    updated[personIndex].cat_has_products = true;
    updated[personIndex].sub_categories = [];
    updated[personIndex].products = [];
    updated[personIndex].showDescriptionForId = null;
    updated[personIndex].showDescriptionForType = null;

    // Fetch Products
    const products = await fetchProducts(categoryObj.id);
    if (products.length > 0) {
      updated[personIndex].showProductsForType = "category";
      updated[personIndex].showProductsForId = categoryObj.id;
      updated[personIndex].showProductsForIdParent = null;
      updated[personIndex].products = products;
      updated[personIndex].cat_has_products = true;
    } else {
      updated[personIndex].showProductsForType = null;
      updated[personIndex].showProductsForId = null;
      updated[personIndex].showProductsForIdParent = null;
      updated[personIndex].products = [];
      updated[personIndex].cat_has_products = false;
    }

    // Fetch Subcategories with `is_hidden=false`
    const subcats = await CategoryService.index(
      { parent_id: categoryObj.id, is_hidden: false, is_active: 1, with: 'media' },
      api,
      auth?.accessToken ? false : true
    );

    if (subcats.length > 0) {
      updated[personIndex].cat_has_subcategories = true;
      updated[personIndex].sub_categories = subcats;
    } else {
      updated[personIndex].cat_has_subcategories = false;
      updated[personIndex].sub_categories = [];
    }

    setSelectedServices(updated);
  };


  // When select a subcategory
  const handleSubCategorySelect = async (personIndex, subcatObj) => {
    const updated = [...selectedServices];
    const currentlySelected = updated[personIndex].subCategory?.id === subcatObj.id;
    console.log({ currentlySelected })
    if (currentlySelected) {
      // Deselect the subcategory
      updated[personIndex].subCategory = null;
      updated[personIndex].product = null;
      updated[personIndex].sub_cat_has_products = true;
      updated[personIndex].showDescriptionForId = null;
      updated[personIndex].showDescriptionForType = null;
      updated[personIndex].showProductsForType = null;
      updated[personIndex].showProductsForId = null;
      updated[personIndex].products = [];

      setSelectedServices(updated);
      return;
    }
    updated[personIndex].subCategory = subcatObj;
    updated[personIndex].product = null;
    updated[personIndex].sub_cat_has_products = true;
    updated[personIndex].showDescriptionForId = null;
    updated[personIndex].showDescriptionForType = null;

    // Fetch Products under the selected subcategory
    const products = await fetchProducts(subcatObj.id);
    const products_parent = await fetchProducts(updated[personIndex].category.id);
    if (products.length > 0) {
      updated[personIndex].showProductsForType = "subCategory";
      updated[personIndex].showProductsForId = subcatObj.id;
      updated[personIndex].showProductsForIdParent = subcatObj.parent_id;
      updated[personIndex].products = products;
      updated[personIndex].productsCat = products_parent;
    } else {
      updated[personIndex].sub_cat_has_products = false;
      updated[personIndex].showProductsForType = null;
      updated[personIndex].showProductsForId = null;
      updated[personIndex].showProductsForIdParent = subcatObj.parent_id;
      updated[personIndex].products = [];
      updated[personIndex].productsCat = products_parent;
    }

    setSelectedServices(updated);
  };

  const handleProductSelect = (personIndex, prodObj) => {
    const updated = [...selectedServices];
    updated[personIndex].product = prodObj;
    setSelectedServices(updated);
  };

  // Add or remove person
  const addPerson = () => {
    if (selectedServices.length < 4) {
      setSelectedServices([
        ...selectedServices,
        { domain: null, category: null, subCategory: null, product: null, cat_has_products: false, categories: [], sub_categories: [], products: [], sub_cat_has_products: false, cat_has_subcategories: false, showDescriptionForId: null, showDescriptionForType: null, showProductsForId: null, showProductsForType: null }
      ]);
    }
  };
  const removePerson = (idx) => {
    setSelectedServices(selectedServices.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const anyEmpty = selectedServices.some(s => !s.product);
    if (anyEmpty) {
  notify.warn(t('please_select_a_product'));
      return;
    }

    const latestSessionState = JSON.parse(sessionStorage.getItem('state') || '{}');
    const latestAddress = latestSessionState.address || address;
    const stateToSave = {
      ...latestSessionState,
      selectedServices,
      duo: (selectedServices.length === 2 || selectedServices.length === 4) && isActive,
      address: latestAddress,
      phone
    };
    sessionStorage.setItem('state', JSON.stringify(stateToSave));

    // Merge updated product(s) into newBooking if we are in edit mode
    const isEditMode = sessionStorage.getItem("editBookingMode") === "true";
    if (isEditMode) {
      const existingNew = sessionStorage.getItem("newBooking");
      const parsedNew = existingNew ? JSON.parse(existingNew) : {};

      // Use the latest address from state in sessionStorage!
      const freshState = JSON.parse(sessionStorage.getItem('state') || '{}');
      const updatedAddress = freshState?.address || address;

      const updatedProduct = selectedServices?.[0]?.product;

      const mergedNewBooking = {
        ...parsedNew,
        address: updatedAddress,
        products: selectedServices.map(s => s.product),
        product: updatedProduct,
        selectedServices: selectedServices,
        duo: (selectedServices.length === 2 || selectedServices.length === 4) && isActive,
      };
      sessionStorage.setItem("newBooking", JSON.stringify(mergedNewBooking));
    }

    if ((selectedServices.length === 2 || selectedServices.length === 4) && !isActive && !hasModalShown) {
      openModalservice();
      setHasModalShown(true);
      return false;
    }

    // Track the service selection step as add_to_cart.
    try {
      const resolvedTrackingUser = await resolveTrackingUser();
      const items = buildItemsFromSelectedServices(selectedServices, {
        address: latestAddress,
      });
      const trackingUserData = buildUserDataObject({
        user: resolvedTrackingUser,
        address: latestAddress,
        phoneNumber: phone || resolvedTrackingUser?.phone || auth?.user?.phone,
        emailAddress: resolvedTrackingUser?.email || auth?.user?.email,
        isConnectedUser: Boolean(auth?.user?.id),
      });
      if (items.length > 0) {
        const value = calculateValueFromItems(items);
        const itemsSignature = items.map((item) => item.item_id).join(",");
        pushDataLayerEvent(
          "add_to_cart",
          {
            user_data: trackingUserData,
            ...buildGtmEventFields({
              language: i18n.language,
              user: resolvedTrackingUser || auth?.user,
            }),
            ecommerce: buildEcommerceObject({
              currency: "CHF",
              value,
              items,
            }),
          },
          {
            dedupeKey: createEventDedupeKey(
              "add_to_cart",
              itemsSignature,
              latestAddress?.zip_code,
              selectedServices.length
            ),
            dedupeTtlMs: 30000,
          }
        );
      }
    } catch (trackingError) {
      console.error("Failed to push add_to_cart event:", trackingError);
    }

    navigate('/choose-therapist', { state: stateToSave });
  };

  const toggleDescription = (type, id, personIndex) => {
    const updated = [...selectedServices];
    const currentlySelected = updated[personIndex].showDescriptionForId === id;
    console.log({ currentlySelected })
    if (currentlySelected) {
      updated[personIndex].showDescriptionForId = null;
      updated[personIndex].showDescriptionForType = null;
      setSelectedServices(updated);
      return;
    }
    // updated[personIndex].showProductsForType = null;
    // updated[personIndex].showProductsForId = null;
    updated[personIndex].showDescriptionForId = id;
    updated[personIndex].showDescriptionForType = type;
    // if (type === "category") {
    //   updated[personIndex].category = null;
    // }
    // else if (type === "subCategory") {
    //   updated[personIndex].subCategory = null;
    // }
    // updated[personIndex].product = null;
    setSelectedServices(updated);
  };
  const handleGoBackClick = (personIndex) => {
    const updated = [...selectedServices];
    const currentlySelected = updated[personIndex];

    if (currentlySelected && currentlySelected.domain && !currentlySelected.category) {
      updated[personIndex].domain = null;
      setSelectedServices(updated);
      return;
    } else if (currentlySelected && currentlySelected.category && !currentlySelected.subCategory && currentlySelected.cat_has_subcategories) {
      // updated[personIndex].domain = null;
      updated[personIndex].category = null;
      updated[personIndex].product = null;
      // updated[personIndex].categories = [];
      updated[personIndex].products = [];
      updated[personIndex].showProductsForId = null;
      setSelectedServices(updated);
      return;
    } else if (currentlySelected && currentlySelected.subCategory) {
      updated[personIndex].category = null;
      updated[personIndex].subCategory = null;
      updated[personIndex].product = null;
      updated[personIndex].showProductsForId = null;
      updated[personIndex].products = [];
      setSelectedServices(updated);
      return;
    } else if (currentlySelected && currentlySelected.category && !currentlySelected.subCategory && currentlySelected.cat_has_subcategories) {
      updated[personIndex].domain = null;
      updated[personIndex].category = null;
      updated[personIndex].product = null;
      updated[personIndex].categories = [];
      updated[personIndex].products = [];
      updated[personIndex].showProductsForId = null;
      setSelectedServices(updated);
      return;

    }

  };
  console.log("selects", selectedServices)
  return (
    <>
      <MenuHeader />
      <Nav page="service" />
      <div className={`container container-location container-service booking-service-page booking-service-page--normal row-${translatedSelectedServices.length}`}>
        {translatedSelectedServices[0].domain && translatedSelectedServices.length === 1 ? (
          <Container className="booking-service-page__title-row" sx={{ display: 'flex', padding: '10px 0 !important' }}>
            <ThemeProvider theme={theme}>
              <Button variant="outlined" onClick={() => handleGoBackClick(0)} color='dark' type="button"
                sx={{ textTransform: 'none', borderRadius: '15px', width: '100%', maxWidth: '50px', minHeight: '40px' }}>
                <img className="https-app" src={goback} alt="Info" />
              </Button>
            </ThemeProvider>
            <h2 className='h-goback'>{t('choose_service_type')}</h2>
          </Container>
        ) : (
          <h2>
            {selectedServices.length > 1
              ? t('choose_service_type_plus')
              : t('choose_service_type')}
          </h2>
        )}
        <form className='service-form booking-service-page__form' onSubmit={handleSubmit}>
          <div className='content-row'>
            {translatedSelectedServices.map((person, idx) => (
              <div key={idx} className="person-services">
                {selectedServices.length > 1 && (
                  <h3 className='person-title'>
                    <span>
                      {translatedSelectedServices.length > 1 && translatedSelectedServices[idx].domain && (
                        <img className="https-app" src={goback} alt="Info" onClick={() => handleGoBackClick(idx)} />
                      )}
                      <img className="people" src={people} alt="icon" />
                      {idx === 0 ? t('me') : `${t('guest')} ${idx}`}
                    </span>
                    {idx > 0 && (
                      <button
                        type='button'
                        className='person-remove'
                        onClick={() => removePerson(idx)}
                      >
                        <img className='remove' src={remove} alt={t('remove')} />
                      </button>
                    )}
                  </h3>
                )}

                {/* show domain */}
                {(!person.domain || (person.domain && !person.categories.length)) && (
                  <div className="services-container">
                    {translatedDomains.map(dom => {
                      const isSelected = person.domain?.id === dom.id;
                      return (
                        <div key={dom.id} className="service-wrapper">
                          <div
                            className={`service-card ${isSelected ? 'selected' : ''}`}
                            style={{
                              background: `linear-gradient(135deg, rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.4)), url(${dom?.media[0]?.original_url}) no-repeat center center / cover`,
                              backgroundRepeat: 'no-repeat',
                              backgroundPosition: 'center center',
                              backgroundSize: 'cover',
                            }}
                          >
                            <div className="service-info">
                              <div className="service-text">
                                <h3>{dom.title}</h3>
                                {dom.description !== null && dom.description !== '' && (
                                  <ThemeProvider theme={theme}>
                                    <Button variant="text" color='white' onClick={() => toggleDescription("domain", dom.id, idx)} sx={{ opacity: 0.7, gap: '5px', padding: 0 }}>{t('more_info')} <img src="assets/images/icon-expand.svg" /></Button>
                                  </ThemeProvider>
                                )}
                              </div>
                              {isSelected ? (
                                <IconButton onClick={() => handleDomainSelect(idx, dom)}>
                                  <img src='assets/images/check.png' height={20} width={20} />
                                </IconButton>
                              ) : (
                                <ThemeProvider theme={theme}>
                                  <Button variant="outlined" color='white' onClick={() => handleDomainSelect(idx, dom)} sx={{ borderRadius: '15px' }}>{t('select')}</Button>
                                </ThemeProvider>
                              )}
                            </div>
                          </div>
                          {person.showDescriptionForType === "domain" && person.showDescriptionForId === dom.id && <div className="service-description">{dom.description}</div>}
                        </div>
                      )
                    })}
                  </div>
                )}

                {/* show category */}
                <div className="services-container">
                  {person.categories?.length > 0 && person.categories.map(cat => {
                    const isSelected = person.category?.id === cat.id;
                    return person.domain && (!person.category || (person.category && !person.sub_categories.length) || (person.category && person.cat_has_products && (!person.cat_has_subcategories || (person.cat_has_subcategories && person.category.id === cat.id)))) && (
                      <div key={cat.id} className="service-wrapper">
                        <div
                          className={`service-card ${isSelected ? 'selected' : ''}`}
                          style={{
                            background: `linear-gradient(135deg, rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.5)), url(${cat?.media[0]?.original_url}) no-repeat center center / cover`,
                          }}
                        >
                          <div className="service-info">
                            <div className="service-text">
                              <h3>{cat.title}</h3>
                              {cat.description !== null && cat.description !== '' && (
                                <ThemeProvider theme={theme}>
                                  <Button variant="text" color='white' onClick={() => toggleDescription("category", cat.id, idx)} sx={{ opacity: 0.7, gap: '5px', padding: 0 }}>{t('more_info')} <img src="assets/images/icon-expand.svg" /></Button>
                                </ThemeProvider>
                              )}
                            </div>
                            {isSelected ? (
                              <IconButton onClick={() => handleCategorySelect(idx, cat)}>
                                <img src='assets/images/check.png' height={20} width={20} />
                              </IconButton>
                            ) : (
                              <ThemeProvider theme={theme}>
                                <Button variant="outlined" color='white' onClick={() => handleCategorySelect(idx, cat)} sx={{ borderRadius: '15px' }}>{t('select')}</Button>
                              </ThemeProvider>
                            )}
                          </div>
                        </div>
                        {person.showDescriptionForType === "category" && person.showDescriptionForId === cat.id && <div className="service-description">{cat.description}</div>}
                        {person.showProductsForType === "category" && person.showProductsForId === cat.id && <div className='form-duree yoo'>
                          <div className="form-group serv-form">
                            <div
                              className={`btn-group group-3 ${person.products?.length > 2 ? 'service-product-group--compact' : ''}`}
                              role="group"
                              style={{ '--service-product-count': Math.max(person.products?.length || 1, 1) }}
                            >
                              {(() => {
                                // Trouver le produit avec le plus de réservations
                                const mostBookedProduct = person.products.reduce((max, product) =>
                                  (product.bookings?.length || 0) > (max.bookings?.length || 0) ? product : max,
                                  person.products[0]
                                );
                                return person.products.map(product => (
                                  <button
                                    type="button"
                                    key={product.id}
                                    className={`btn-default 
          ${selectedServices[idx]?.product?.id === product.id ? 'btn-checked' : 'btn-unchecked'} 
          ${product.id === mostBookedProduct.id ? 'product-popular' : ''}`}
                                    onClick={() => handleProductSelect(idx, product)}
                                  >
                                    <span>{product.duration} min</span>
                                    <br />
                                    <span className="price">{product.price} chf</span>
                                    {product.id === mostBookedProduct.id &&
                                      <div className='popular-product'>

                                      </div>
                                    }
                                  </button>
                                ));
                              })()}
                            </div>

                          </div>
                        </div>
                        }{isSelected && person.showProductsForIdParent === cat.id && <div className='form-duree'>
                          <div className="form-group serv-form">
                            <div
                              className={`btn-group group-3 ${person.productsCat?.length > 2 ? 'service-product-group--compact' : ''}`}
                              role="group"
                              style={{ '--service-product-count': Math.max(person.productsCat?.length || 1, 1) }}
                            >
                              {(() => {
                                // Trouver le produit avec le plus de réservations dans productsCat
                                const mostBookedProduct = person.productsCat.reduce((max, product) =>
                                  (product.bookings?.length || 0) > (max.bookings?.length || 0) ? product : max,
                                  person.productsCat[0]
                                );
                                return person.productsCat.map(product => (
                                  <button
                                    type="button"
                                    key={product.id}
                                    className={`btn-default 
          ${selectedServices[idx]?.product?.id === product.id ? 'btn-checked' : 'btn-unchecked'} 
          ${product.id === mostBookedProduct.id ? 'product-popular' : ''}`}
                                    onClick={() => handleProductSelect(idx, product)}
                                  >
                                    <span>{product.duration} min</span>
                                    <br />
                                    <span className="price">{product.price} chf</span>
                                    {product.id === mostBookedProduct.id &&
                                      <div className='popular-product'>

                                      </div>
                                    }
                                  </button>
                                ));
                              })()}
                            </div>

                          </div>
                        </div>
                        }
                      </div>
                    );
                  })}
                </div>

                {/* show subCategories */}
                {person.domain && person.category && person.sub_categories.length > 0 && (!person.subCategory || (person.subCategory && !person.products.length) || (person.subCategory && person.sub_cat_has_products)) && (
                  <div className="services-container">
                    {person.sub_categories.map(sc => {
                      const isSelected = person.subCategory?.id === sc.id;
                      return (
                        <div key={sc.id} className="service-wrapper">
                          <div
                            className={`service-card ${isSelected ? 'selected' : ''}`}
                            style={{
                              background: `linear-gradient(135deg, rgba(0,0,0,0.8), rgba(0,0,0,0.5)), url(${sc?.media[0]?.original_url}) center/cover no-repeat`
                            }}
                          >
                            <div className="service-info">
                              <div className="service-text">
                                <h3>{sc.title}</h3>
                                {sc.description !== null && sc.description !== '' && (
                                  <ThemeProvider theme={theme}>
                                    <Button variant="text" color='white' onClick={() => toggleDescription("subCategory", sc.id, idx)} sx={{ opacity: 0.7, gap: '5px', padding: 0 }}>{t('more_info')} <img src="assets/images/icon-expand.svg" /></Button>
                                  </ThemeProvider>
                                )}
                              </div>
                              {isSelected ? (
                                <IconButton onClick={() => handleSubCategorySelect(idx, sc)}>
                                  <img src='assets/images/check.png' height={20} width={20} />
                                </IconButton>
                              ) : (
                                <ThemeProvider theme={theme}>
                                  <Button variant="outlined" color='white' onClick={() => handleSubCategorySelect(idx, sc)} sx={{ borderRadius: '15px' }}>{t('select')}</Button>
                                </ThemeProvider>
                              )}
                            </div>
                          </div>
                          {person.showDescriptionForType === "subCategory" && person.showDescriptionForId === sc.id && <div className="service-description">{sc.description}</div>}
                          {person.showProductsForType === "subCategory" && person.showProductsForId === sc.id && <div className='form-duree'>
                            <div className="form-group serv-form">
                              <div
                                className={`btn-group group-3 ${person.products?.length > 2 ? 'service-product-group--compact' : ''}`}
                                role="group"
                                style={{ '--service-product-count': Math.max(person.products?.length || 1, 1) }}
                              >
                                {(() => {
                                  // Trouver le produit avec le plus de bookings
                                  const mostBookedProduct = person.products.reduce((max, product) =>
                                    (product.bookings?.length || 0) > (max.bookings?.length || 0) ? product : max,
                                    person.products[0]
                                  );
                                  return person.products.map(product => (
                                    <button
                                      type="button"
                                      key={product.id}
                                      className={`btn-default 
          ${selectedServices[idx]?.product?.id === product.id ? 'btn-checked' : 'btn-unchecked'} 
          ${product.id === mostBookedProduct.id ? 'product-popular' : ''}`}
                                      onClick={() => handleProductSelect(idx, product)}
                                    >
                                      <span>{product.duration} min</span>
                                      <br />
                                      <span className="price">{product.price} chf</span>
                                      {product.id === mostBookedProduct.id &&
                                        <div className='popular-product'>

                                        </div>
                                      }
                                    </button>
                                  ));
                                })()}
                              </div>

                            </div>
                          </div>
                          }
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}

          </div>
          {showDuoToggle && (
            <div className='duo'>
              <div className='info'>
                {t('duo_massage')} <i className="fa fa-info-circle"></i>
              </div>
              <div className='toggle'>
                <div
                  className={`toggle-switch ${isActive ? 'active' : ''}`}
                  onClick={toggleSwitch}
                />
              </div>
            </div>
          )}
          <Container className="booking-service-page__actions" sx={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap', padding: '0 !important' }}>
            <ThemeProvider theme={theme}>
              <Button className="booking-service-page__action-button booking-service-page__add-treatment-button" variant="outlined" color='dark' type="button"
                onClick={addPerson} sx={{ textTransform: 'none', borderRadius: '15px', width: '100%', maxWidth: '315px', minHeight: '57px', fontSize: '16px !important', fontWeight: '700 !important' }}>{t('add_person')}</Button>
              <Button className="booking-service-page__action-button" variant="contained" color='dark_yellow' type="submit" sx={{ textTransform: 'none', borderRadius: '15px', width: '100%', maxWidth: '315px', minHeight: '57px' }}>{t('next')}</Button>
            </ThemeProvider>
          </Container>
        </form >
      </div >
      <Modal
        isOpen={modalIsOpenService}
        onRequestClose={closeModalservice}
        style={customStyles}
        contentLabel="Example Modal"
      >
        <div className="zone-not-served-container">
          <div className="popup-box">
            <button onClick={closeModalservice} className="close-btn"><img src={btnInfo} alt="Fermer" /></button>
            <p> {t('modal_message')}</p>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={duoModalOpenLocal}
        onRequestClose={() => { setDuoModalOpenLocal(false); try { sessionStorage.removeItem('pendingDuoToggle'); } catch (err) {} }}
        style={customStyles}
      >
        <h2>{t('commande_edit:sorry', { ns: 'commande_edit' })}</h2>
        <p>
          {t('commande_edit:duo_massage_disclaimer', { ns: 'commande_edit' })}
          <br /><br />
          {t('commande_edit:duo_massage_disclaimer2', { ns: 'commande_edit' })}
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 18 }}>
          <button className="btn btn-secondary" onClick={() => { setDuoModalOpenLocal(false); try { sessionStorage.removeItem('pendingDuoToggle'); } catch (err) {} }}>
            {t('commande_edit:close', { ns: 'commande_edit' })}
          </button>
          <button className="btn btn-primary" onClick={() => { try { sessionStorage.removeItem('pendingDuoToggle'); } catch (err) {} setDuoModalOpenLocal(false); navigate('/'); }}>
            {t('commande_edit:duo_booking', { ns: 'commande_edit' })}
          </button>
        </div>
      </Modal>
    </>
  );
}

export default ChooseService;
